// src/components/Header.js
import React, { useState } from "react";
import Container from "./Container";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Phone, PhoneCall } from "lucide-react";

export default function Header() {
  const [open, setOpen] = useState(false);
  const { i18n, t } = useTranslation();
  const location = useLocation();

  // 🔧 여기만 바꾸면 전체 반영
  const PHONE = "010-5956-6965";

  const toggleLang = () =>
    i18n.changeLanguage(i18n.language === "ko" ? "en" : "ko");

  const linkBase =
    "text-sm text-slate-600 hover:text-blue-900 transition-colors";
  const active = "text-blue-900 font-medium underline underline-offset-4";
  const isCruiseActive = location.pathname.startsWith("/services/cruise");
  const isEmploymentActive =
    location.pathname === "/services/employment" ||
    location.pathname.startsWith("/employment");
  const isServiceActive = isCruiseActive || isEmploymentActive;

  const LOGO_SRC = "/assets/brand/logo.png";

  return (
    <header className="sticky top-0 z-40 border-b bg-white/70 backdrop-blur-xl">
      <Container className="flex h-16 items-center justify-between">
        {/* 로고 */}
        <NavLink
          to="/"
          aria-label={t("nav.home")}
          className="flex items-center gap-2"
        >
          <img
            src={LOGO_SRC}
            alt={t("brand")}
            className="h-8 w-auto object-contain"
            draggable={false}
          />
          <span className="sr-only">{t("brand")}</span>
        </NavLink>

        {/* 데스크톱 내비 */}
        <nav className="hidden items-center gap-6 md:flex" aria-label="Primary">
          <NavLink
            to="/about-us"
            className={({ isActive }) =>
              isActive ? `${linkBase} ${active}` : linkBase
            }
          >
            {t("nav.about")}
          </NavLink>

          {/* 소식 / News */}
          <NavLink
            to="/news"
            className={({ isActive }) =>
              isActive ? `${linkBase} ${active}` : linkBase
            }
          >
            {t("nav.news", "뉴스&이벤트")}
          </NavLink>

          {/* 서비스 / Services (Hover dropdown) */}
          <div className="group relative">
            <NavLink
              to="/services/employment"
              className={isServiceActive ? `${linkBase} ${active}` : linkBase}
            >
              {t("nav.services", "서비스")}
            </NavLink>
            <div className="invisible absolute left-1/2 top-full z-50 w-40 -translate-x-1/2 translate-y-1 opacity-0 transition duration-150 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100">
              <div className="mt-2 rounded-xl border bg-white p-1.5 shadow-lg ring-1 ring-slate-100">
                <NavLink
                  to="/services/cruise"
                  className={`block rounded-lg px-3 py-2 text-sm transition ${
                    isCruiseActive
                      ? "bg-blue-50 font-medium text-blue-800"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {t("nav.serviceCruise", "크루즈")}
                </NavLink>
                <NavLink
                  to="/services/employment"
                  className={`block rounded-lg px-3 py-2 text-sm transition ${
                    isEmploymentActive
                      ? "bg-blue-50 font-medium text-blue-800"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {t("nav.serviceEmployment", "채용")}
                </NavLink>
              </div>
            </div>
          </div>

          <NavLink
            to="/faq"
            className={({ isActive }) =>
              isActive ? `${linkBase} ${active}` : linkBase
            }
          >
            {t("nav.faq")}
          </NavLink>

          <NavLink
            to="/inquiry"
            className={({ isActive }) =>
              isActive ? `${linkBase} ${active}` : linkBase
            }
            aria-label={t("nav.inquiry")}
          >
            {t("nav.inquiry")}
          </NavLink>
        </nav>

        {/* 우측: 전화/언어/CTA/관리자 */}
        <div className="flex items-center gap-3">
          {/* 데스크톱: 전화번호 배지 */}
          <a
            href={`tel:${PHONE.replace(/[^+\d]/g, "")}`}
            className="hidden items-center gap-2 rounded-xl bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 ring-1 ring-blue-200 hover:bg-blue-100 md:flex"
            aria-label={t("contact.callUs")}
            title={t("contact.callUs")}
          >
            <Phone className="h-4 w-4" />
            <span className="tabular-nums">{PHONE}</span>
          </a>

          {/* 데스크톱: 관리자 로그인 버튼 */}
          <NavLink
            to="/login"
            className="hidden md:inline-flex items-center justify-center rounded-lg border px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
            aria-label={t("nav.admin", "관리자")}
            title={t("nav.admin", "관리자")}
          >
            {t("nav.admin", "관리자")}
          </NavLink>

          <button
            onClick={toggleLang}
            className="hidden md:inline-flex rounded-lg border px-3 py-2 text-sm"
            aria-label={t("lang.toggle")}
            title={t("lang.toggle")}
          >
            {i18n.language === "ko" ? t("lang.en") : t("lang.ko")}
          </button>

          {/* 지원하기(온라인 신청) */}
          <NavLink
            to="/contact-us"
            className="hidden md:inline-flex items-center justify-center rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            aria-label={t("nav.apply")}
            title={t("nav.apply")}
          >
            {t("nav.apply")}
          </NavLink>

          {/* 모바일: 콜 버튼 + 메뉴 버튼 */}
          <div className="flex items-center gap-2 md:hidden">
            <a
              href={`tel:${PHONE.replace(/[^+\d]/g, "")}`}
              className="grid h-9 w-9 place-items-center rounded-lg bg-blue-600 text-white ring-1 ring-blue-300"
              aria-label={t("contact.callUs")}
              title={t("contact.callUs")}
            >
              <PhoneCall className="h-4 w-4" />
            </a>
            <button
              className="rounded-lg border px-3 py-2 text-sm"
              onClick={() => setOpen((v) => !v)}
              aria-label={t("nav.menu")}
              aria-expanded={open}
              aria-controls="mobile-menu"
            >
              {t("nav.menu")}
            </button>
          </div>
        </div>
      </Container>

      {/* 모바일 메뉴 */}
      {open && (
        <div id="mobile-menu" className="border-t bg-white md:hidden">
          <Container className="flex flex-col gap-1 py-3">
            <NavLink
              to="/about-us"
              onClick={() => setOpen(false)}
              className="rounded-lg px-2 py-2 text-base text-slate-700"
            >
              {t("nav.about")}
            </NavLink>

            {/* 소식 / News (모바일) */}
            <NavLink
              to="/news"
              onClick={() => setOpen(false)}
              className="rounded-lg px-2 py-2 text-base text-slate-700"
            >
              {t("nav.news", "뉴스&이벤트")}
            </NavLink>

            {/* 서비스 / Services (모바일) */}
            <div className="rounded-lg px-2 py-2 text-base font-medium text-slate-700">
              {t("nav.services", "서비스")}
            </div>
            <NavLink
              to="/services/cruise"
              onClick={() => setOpen(false)}
              className="ml-3 rounded-lg px-2 py-2 text-base text-slate-700"
            >
              {t("nav.serviceCruise", "크루즈")}
            </NavLink>
            <NavLink
              to="/services/employment"
              onClick={() => setOpen(false)}
              className="ml-3 rounded-lg px-2 py-2 text-base text-slate-700"
            >
              {t("nav.serviceEmployment", "채용")}
            </NavLink>

            <NavLink
              to="/faq"
              onClick={() => setOpen(false)}
              className="rounded-lg px-2 py-2 text-base text-slate-700"
            >
              {t("nav.faq")}
            </NavLink>

            <NavLink
              to="/inquiry"
              onClick={() => setOpen(false)}
              className="rounded-lg px-2 py-2 text-base text-slate-700"
            >
              {t("nav.inquiry")}
            </NavLink>

            <NavLink
              to="/contact-us"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              {t("nav.apply")}
            </NavLink>

            {/* 모바일: 관리자 로그인 링크 */}
            <NavLink
              to="/login"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center rounded-xl border px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              {t("nav.admin", "관리자")}
            </NavLink>

            <hr className="my-3" />

            {/* 연락처 묶음(모바일) */}
            <div className="grid gap-2 rounded-xl border bg-slate-50 p-3">
              <a
                href={`tel:${PHONE.replace(/[^+\d]/g, "")}`}
                className="inline-flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm font-medium text-blue-700 ring-1 ring-blue-200"
              >
                <Phone className="h-4 w-4" />
                <span className="tabular-nums">{PHONE}</span>
              </a>
            </div>

            <button
              onClick={() => {
                toggleLang();
                setOpen(false);
              }}
              className="mt-2 w-fit rounded-lg border px-3 py-2 text-sm"
              aria-label={t("lang.toggle")}
              title={t("lang.toggle")}
            >
              {i18n.language === "ko" ? t("lang.en") : t("lang.ko")}
            </button>
          </Container>
        </div>
      )}
    </header>
  );
}
